import cx from "classnames";
import { useEffect, useState } from "react";
import {
  handleConnectPaymentPreferenceProps,
  handleDisconnectPaymentPreferenceProps,
} from ".";
import { useToast } from "../../../../../hooks/use-toast.hook";
import { PaymentPreference } from "../../../../../types/payment-preference/payment-preference.type";
import { Button, TextInput } from "../../../../../_components";
import { NewActionModal } from "../../../../../_components/action2-modal.component";
import Label from "../../../../../_components/typography/label.typography";
import { Fold } from "../../../../../_utils/extensions/typescript-utils";
import Validator from "../../../../../_utils/validator";
import { RenderStatus } from "./link-razorpay-component";
import { useAppService } from "../../../../../hooks/use-app-service";

type Status = "idle" | "loading" | "success" | "error";
interface Props {
  community: any;
  paymentPreference: PaymentPreference | null | undefined;
  status: Status;
  handleDisconnectPaymentPreference: ({
    onSuccess,
    onFailed,
  }: handleDisconnectPaymentPreferenceProps) => void;
  handleConnectPaymentPreference: ({
    payload,
    onSuccess,
    onFailed,
  }: handleConnectPaymentPreferenceProps) => void;
}

/**
 * Component to connect bank account
 */
export default function LinkBankAccountView({
  status,
  paymentPreference,
  handleConnectPaymentPreference,
  handleDisconnectPaymentPreference,
}: Props) {
  const { analyticsService } = useAppService();

  const bankAccount = paymentPreference?.bankAccount;
  const integrationStatus = paymentPreference?.integrationStatus;
  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [ReEnterBankAccountNumber, setReEnterBankAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [activeDiscount, setActiveDiscount] = useState(false);

  const [errors, setErrors] = useState<any>({
    businessName: "",
    businessType: "",
    bankAccountNumber: "",
    ReEnterBankAccountNumber: "",
    ifscCode: "",
    beneficiaryName: "",
  });

  const isBankAccountConnected =
    Validator.hasValue(bankAccount?.bankAccountNumber) &&
    paymentPreference?.paymentMethod === "bank-account" &&
    integrationStatus === "completed";

  const toast = useToast();

  //  Initialize bank account details
  useEffect(() => {
    if (!bankAccount) {
      return;
    }
    if (bankAccount.businessName) {
      setBusinessName(bankAccount.businessName);
      setBusinessType(bankAccount.businessType ?? "");
      setBankAccountNumber(bankAccount.bankAccountNumber ?? "");
      setIfscCode(bankAccount.ifscCode ?? "");
      setBeneficiaryName(bankAccount.beneficiaryName ?? "");
    }
  }, [bankAccount]);

  function onFormSubmit() {    
    if (paymentPreference?.bankAccount?.bankAccountNumber) {
      setActiveDiscount(true);
      analyticsService.track("bank-account-disconnect-request")
    } else {
      // Check if Bank Account Number and Re-enter Bank Account Number are same
      if (bankAccountNumber !== ReEnterBankAccountNumber) {
        setErrors((prev: any) => ({
          ...prev,
          bankAccountNumber:
            "Bank Account Number and Re-enter Bank Account Number should be same",
        }));
        return;
      } else {
        setErrors((prev: any) => ({
          ...prev,
          bankAccountNumber: "",
        }));
      }
      const payload = {
        paymentMethod: "bank-account",
        businessName: businessName,
        businessType: businessType,
        bankAccountNumber: bankAccountNumber,
        ifscCode: ifscCode,
        beneficiaryName: beneficiaryName,
      };
      handleConnectPaymentPreference({
        payload: payload,
        onSuccess: () => {
          toast.addToast(
            "Request to update payment preference sent",
            "Please wait for a while to update your payment preference",
            "success"
          );
        },
        onFailed: () => {},
      });
      analyticsService.track("bank-account-connect-request")
    }
  }
  return (
    <div>
      <form
        className="theme-bg-default px-3 py-6 mt-4 rounded"
        onSubmit={(e) => {
          e.preventDefault();
          
          onFormSubmit();
        }}>
        <div className="flex flex-col sm:flex-row gap-2 justify-between font-semibold pb-4 sm:pb-0">
          <Label size="body1" variant="t1" translate>
            Link your bank account
          </Label>
          {["bank-account", "not-set", undefined, null].includes(
            paymentPreference?.paymentMethod
          ) &&
            Validator.hasValue(integrationStatus) && (
              <RenderStatus integrationStatus={integrationStatus} />
            )}
        </div>

        <div className="flex flex-col gap-4 py-2 mt-4">
          <Label size="body" variant="t2">
            BUSINESS DETAILS
          </Label>

          <CTextInput
            label={"Business Name"}
            readOnly={bankAccount?.beneficiaryName}
            placeholder="Enter business name"
            defaultValue={businessName}
            onChange={(e) => setBusinessName(e)}
          />
          <CTextInput
            label={"Business Type"}
            readOnly={bankAccount?.beneficiaryName}
            placeholder="Enter business type"
            defaultValue={businessType}
            onChange={(e) => setBusinessType(e)}
          />
        </div>
        <div className="flex flex-col gap-4 py-2 mt-4">
          <Label>BANK ACCOUNT DETAILS</Label>

          <CTextInput
            label={"Bank account number"}
            readOnly={bankAccount?.bankAccountNumber}
            placeholder="Enter account number"
            defaultValue={bankAccountNumber}
            onChange={(e) => setBankAccountNumber(e)}
            type="number"
          />
          <CTextInput
            label={"Re-enter account number"}
            readOnly={bankAccount?.bankAccountNumber}
            hidden={bankAccount?.bankAccountNumber ? true : false}
            placeholder="Re enter account number"
            defaultValue={ReEnterBankAccountNumber}
            onChange={(e) => setReEnterBankAccountNumber(e)}
            type="number"
            error={errors.bankAccountNumber}
          />
          <CTextInput
            label={"IFSC Code"}
            readOnly={bankAccount?.ifscCode}
            placeholder="Enter IFSC code"
            defaultValue={ifscCode}
            onChange={(e) => setIfscCode(e)}
          />
          <CTextInput
            label={"Beneficiary Name"}
            readOnly={bankAccount?.beneficiaryName}
            placeholder="Enter beneficiary Name"
            defaultValue={beneficiaryName}
            onChange={(e) => setBeneficiaryName(e)}
          />
        </div>

        <div className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center place-content-between my-4 w-full">
            <Fold
              value={paymentPreference?.bankAccount?.bankAccountNumber}
              ifPresent={() => (
                <Button
                  label="Disconnect"
                  isLoading={status === "loading"}
                  btnColor="red"
                />
              )}
              ifAbsent={() => (
                <Button label={"Connect"} isLoading={status === "loading"} />
              )}
            />
          </div>

          <Fold
            value={
              integrationStatus === "pending" &&
              Validator.hasValue(bankAccount?.bankAccountNumber)
            }
            ifPresent={function (): JSX.Element {
              return (
                <span className={cx("text-xs ")}>
                  <span className="font-semibold">Note:</span> Account
                  verification will take 2 - 8 hours to get verified. Contact
                  <span>
                    <a href="mailto:support@pensil.in"> support@pensil.in </a>
                  </span>
                  for more info
                </span>
              );
            }}
          />
        </div>
      </form>

      <NewActionModal
        title={"Are you sure you want to disconnect bank account?"}
        active={activeDiscount}
        children={
          <div className="flex flex-col gap-3 items-center place-content-center mx-4 mb-4 mt-3">
            <Label variant="t1" className="theme-text-danger">
              Disconnect Bank Account
            </Label>
          </div>
        }
        setActive={setActiveDiscount}
        labelCancel="Cancel"
        labelSubmit="Disconnect"
        btnColor="red"
        onCancelSubmit={() => {
          setActiveDiscount(false);
        }}
        onSubmit={() => {
          setActiveDiscount(false);
          handleDisconnectPaymentPreference({
            onSuccess: () => {
              toast.addToast(
                "Bank account disconnected",
                "Your bank account has been disconnected successfully",
                "success"
              );
              setBusinessName("");
              setBusinessType("");
              setBankAccountNumber("");
              setIfscCode("");
              setBeneficiaryName("");
            },
            onFailed: () => {},
          });
        }}
      />
    </div>
  );
}

// Input component with label
interface TextInputProps {
  label: string;
  type?: string;
  placeholder?: string;
  className?: string;
  noMargin?: boolean;
  hidden?: boolean;
  readOnly?: string | null;
  defaultValue?: string;
  onChange?: (e: any) => void;
  error?: string;
}
function CTextInput({
  label,
  type = "text",
  placeholder,
  className,
  noMargin,
  hidden,
  readOnly,
  defaultValue,
  error,
  onChange = () => {},
}: TextInputProps) {
  const isReadOnly = Validator.hasValue(readOnly);
  return (
    <div
      className={cx("flex flex-col gap-1", {
        hidden: hidden,
      })}>
      <Label variant="t1">{label}</Label>
      <TextInput
        type={type}
        noMargin
        required={!isReadOnly}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        className={cx("theme-bg-surface", {
          "theme-text-subtitle-2 cursor-not-allowed": isReadOnly,
        })}
        defaultValue={defaultValue}
        placeholder={placeholder}
        onChange={(e) => onChange(e)}
        errors={[error]}
      />
    </div>
  );
}
